import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import LazyBookCover from './LazyBookCover';
import { Skeleton } from '@/components/ui/skeleton';

export default function TrendingBooks({ books, loading }) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = dir === 'left' ? -200 : 200;
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Skeleton className="w-4 h-4 rounded bg-cream-dark" />
          <Skeleton className="h-4 w-44 bg-cream-dark" />
        </div>
        <div className="flex gap-3 overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[80px]">
              <Skeleton className="aspect-[2/3] rounded-lg bg-cream-dark" />
              <Skeleton className="h-3 w-16 mt-2 bg-cream-dark" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!books?.length) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-terra" />
          <h3 className="text-sm font-semibold text-ink">Trending on ReadTheRoom</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => scroll('left')}
            className="p-1 rounded-full hover:bg-cream-dark transition-colors text-ink-muted hover:text-ink"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="p-1 rounded-full hover:bg-cream-dark transition-colors text-ink-muted hover:text-ink"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-1"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {books.map((book) => (
          <button
            key={book.id || book.google_books_id}
            onClick={() => book.id ? navigate(`/books/${book.id}`) : navigate(`/books/google/${book.google_books_id}`)}
            className="flex-shrink-0 w-[80px] text-left group"
            style={{ scrollSnapAlign: 'start' }}
          >
            <div className="aspect-[2/3] rounded-lg overflow-hidden bg-cream-dark shadow-sm group-hover:shadow-md transition-shadow">
              <LazyBookCover
                src={book.cover_url || book.thumbnail}
                alt={book.title}
                className="group-hover:scale-105 transition-transform duration-300"
                iconClassName="w-5 h-5"
              />
            </div>
            <p className="text-xs font-medium text-ink mt-1.5 line-clamp-2 leading-tight group-hover:text-terra transition-colors font-heading">
              {book.title}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
